<template>
  <div>
    <v-card-title class="pa-1 mt-1">
      <h4 v-html="field.label"></h4>
    </v-card-title>

    <v-container v-if="loading" fill-height>
      <v-progress-circular :width="3" :size="25" indeterminate color="blue" />
    </v-container>
    <v-btn v-else-if="fullscreenMode" icon @click="open()">
      <v-icon>edit</v-icon>
    </v-btn>

    <div>
      <froala v-if="show && !loading" v-model="newValue" :tag="'div'" :config="config" />
    </div>

    <!-- <froalaView v-model="newValue" /> -->
  </div>
</template>

<script  setup>
import { useStore } from 'vue2-helpers/vuex';
import { ref, watch, nextTick, computed } from 'vue';

import { useApolloClient, useQuery } from '@vue/apollo-composable';

import FroalaEditor from 'froala-editor';

import _ from 'lodash';
import { getGraphqlSchema } from '../../graphql/index';
import { getMediaUrl } from '../../utils/content';

// eslint-disable-next-line no-undef
const props = defineProps({
  field: { type: Object, default: () => { } },
  value: { type: [Array, Boolean, Number, String], default: null }
});

const store = useStore();

const relativeToAbsoluteUrl = (link, html) => {
  const regex = /((?:url\(|<(?:link|script|img)[^>]+(?:src|href)\s*=\s*)(?!['"]?(?:data|http))['"]?)([^'"\)\s>]+)/ig;
  return html ? html.replaceAll(regex, `$1${link}$2`) : null;
};

const absoluteToRelative = (link, html) => (html ? html.replaceAll(link, '') : null);

// eslint-disable-next-line no-undef
const emit = defineEmits(['input']);

const newValue = ref(props.value);

// watch(() => props.value, value => {
//   console.log("props.value changed")
//   if (props.field.previewMode) {
//     newValue.value = relativeToAbsoluteUrl(store.getters.workspace.settings.VUE_APP_PREVIEW.url, value);
//   } else {
//     newValue.value = value;
//   }
// });

watch(newValue, value => {
  if (value === '') {
    emit('input', null);
  } else if (props.field.previewMode) {
    emit('input', absoluteToRelative(store.getters.workspace.settings.VUE_APP_PREVIEW.url, value));
  } else {
    emit('input', value);
  }
});

const { resolveClient } = useApolloClient();

const apollo = resolveClient();

const fullUrl = path => getMediaUrl(path);

const fullscreenMode = !!props.field.fullscreenMode;

const templates = ref(props.field.templates || []);

const scrollPosition = ref(null);

const show = ref(!fullscreenMode);

const token = 'accessToken';

const inlineCss = props.field.css ? props.field.css : '';

const boilerCss = '';

const config = ref({
  // ampizeFieldTarget: key,
  // key: '2YRUFVRf1XPLEb1IFd1==',
  // toolbarVisibleWithoutSelection: true,
  // toolbarInline: true,
  zIndex: 4,
  height: props.field.height || 200,
  // codeMirror: true,
  // codeMirrorOptions: {
  //   indentWithTabs: true,
  //   lineNumbers: true,
  //   lineWrapping: true,
  //   mode: 'text/html',
  //   tabMode: 'indent',
  //   tabSize: 2
  // },
  // codeBeautifierOptions: {
  //   end_with_newline: true,
  //   indent_inner_html: true,
  //   extra_liners: "['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'ul', 'ol', 'table', 'dl']",
  //   brace_style: 'expand',
  //   indent_char: ' ',
  //   indent_size: 4,
  //   wrap_line_length: 0
  // },
  iframe: true, // Isolates editor on iframe, css from PIM doesn't affect iframe
  iframeStyle: `${boilerCss} ${inlineCss}`, // .fr-view > p { border-top: grey 1px dotted; border-bottom: grey 1px dotted; }
  // iframeStyle: boilerCss + cssCode, // It must have the css of the website, how to do it?
  // videoResponsive: true,
  toolbarButtons: {
    moreText: {
      buttons: ['undo', 'redo', 'paragraphFormat', 'bold', 'italic', 'underline', 'strikeThrough', 'clearFormatting', 'insertLink', 'insertCustomImage', 'insertVideo', 'insertTable', 'html'], // 'noembed' <-- It's a registed button, 'fullscreen'
      buttonsVisible: 20
    },
    moreMisc: {
      buttons: fullscreenMode ? ['close'] : [],
      align: 'right',
      buttonsVisible: 1
    }
  },
  pluginsEnabled: ['inlineClass', 'fullscreen', 'image', 'imageManager', 'video', 'align', 'charCounter', 'codeBeautifier', 'codeView', 'colors', 'draggable', 'entities', 'fontFamily', 'fontSize', 'image', 'link', 'lists', 'paragraphFormat', 'paragraphStyle', 'quote', 'table', 'url', 'wordPaste', 'imageAviary'],
  lineBreakerTags: ['table', 'hr', 'form', 'dl', 'div', 'section', 'iframe'], // 'span.fr-video', '.fr-embedly', '.fr-img-caption',
  htmlAllowedTags: ['a', 'abbr', 'address', 'area', 'article', 'aside', 'audio', 'b', 'base', 'bdi', 'bdo',
    'blockquote', 'br', 'button', 'canvas', 'caption', 'cite', 'code', 'col', 'colgroup', 'datalist',
    'dd', 'del', 'details', 'dfn', 'dialog', 'div', 'dl', 'dt', 'em', 'embed', 'fieldset', 'figcaption',
    'figure', 'footer', 'form', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'header', 'hgroup', 'hr', 'i', 'iframe',
    'img', 'input', 'ins', 'kbd', 'keygen', 'label', 'legend', 'li', 'link', 'main', 'map', 'mark', 'menu',
    'menuitem', 'meter', 'nav', 'noscript', 'object', 'ol', 'optgroup', 'option', 'output', 'p', 'param', 'pre',
    'progress', 'queue', 'rp', 'rt', 'ruby', 's', 'samp', 'script', 'style', 'section', 'select', 'small', 'source',
    'span', 'strike', 'strong', 'sub', 'summary', 'sup', 'table', 'tbody', 'td', 'textarea', 'tfoot', 'th', 'thead',
    'time', 'title', 'tr', 'track', 'u', 'ul', 'var', 'video', 'wbr', 'amp-video', 'amp-audio', 'amp-anim', 'amp-apester-media', 'amp-brid-player',
    'amp-brightcove', 'amp-dailymotion', 'amp-google-vrview-image', 'amp-hulu', 'amp-image-lightbox', 'amp-izlesene', 'amp-jwplayer', 'amp-kaltura-player',
    'amp-o2-player', 'amp-ooyala-player', 'amp-playbuzz', 'amp-reach-player', 'amp-soundcloud', 'amp-springboard-player', 'amp-vimeo', 'amp-youtube', 'amp-facebook',
    'amp-facebook-like', 'amp-facebook-comments', 'amp-gfycat', 'amp-instagram', 'amp-pinterest', 'amp-reddit', 'amp-social-share', 'amp-twitter', 'amp-vine', 'amp-sidebar',
    'amp-accordion', 'amp-fx-flying-carpet', 'amp-iframe', 'amp-ad', 'amp-sticky-ad', 'amp-auto-ads', 'amp-analytics', 'amp-access', 'amp-call-tracking', 'amp-install-serviceworker',
    'amp-fit-text', 'amp-app-banner', 'amp-carousel', 'amp-lightbox', 'amp-animation', 'amp-dynamic-css-classes', 'amp-font', 'amp-viz-vega', 'amp-3q-player', 'amp-user-notification', 'amp-state', 'amp-nexxtv-player', 'amp-experiment', 'amp-list', 'template', 'amp-selector'],
  htmlAllowedEmptyTags: ['textarea', 'a', 'iframe', 'object', 'video', 'style', 'script', '.fa', 'amp-video', 'amp-audio', 'amp-anim', 'amp-apester-media', 'amp-brid-player',
    'amp-brightcove', 'amp-dailymotion', 'amp-google-vrview-image', 'amp-hulu', 'amp-image-lightbox', 'amp-izlesene', 'amp-jwplayer', 'amp-kaltura-player',
    'amp-o2-player', 'amp-ooyala-player', 'amp-playbuzz', 'amp-reach-player', 'amp-soundcloud', 'amp-springboard-player', 'amp-vimeo', 'amp-youtube', 'amp-facebook',
    'amp-facebook-like', 'amp-facebook-comments', 'amp-gfycat', 'amp-instagram', 'amp-pinterest', 'amp-reddit', 'amp-social-share', 'amp-twitter', 'amp-vine', 'amp-sidebar',
    'amp-accordion', 'amp-fx-flying-carpet', 'amp-iframe', 'amp-ad', 'amp-sticky-ad', 'amp-auto-ads', 'amp-analytics', 'amp-access', 'amp-call-tracking', 'amp-install-serviceworker',
    'amp-fit-text', 'amp-app-banner', 'amp-carousel', 'amp-lightbox', 'amp-animation', 'amp-dynamic-css-classes', 'amp-font', 'amp-viz-vega', 'amp-3q-player', 'amp-user-notification', 'amp-state', 'amp-nexxtv-player', 'amp-experiment', 'amp-list', 'template', 'amp-selector'],
  // imageManagerLoadURL: '/admin/media/manager-list',
  // imageManagerLoadParams: { access_token: token },
  // imageManagerLoadMethod: 'GET',
  // imageUploadParam: 'file',
  // imageUploadURL: '/admin/media/upload',
  // imageUploadParams: { access_token: token },
  // imageUploadMethod: 'POST',
  imageOutputSize: true,
  imageMultipleStyles: false,
  // imageStyles: {
  //   'ampize-fixed-layout': 'Fixed size',
  //   'ampize-fixed-height-layout': 'Fixed height',
  //   'ampize-responsive-layout': 'Responsive size',
  //   'ampize-fill-layout': 'Fill available space'
  // },
  inlineClasses: props.field.inlineClasses,
  imageMaxSize: 3 * 1024 * 1024,
  imageAllowedTypes: ['jpeg', 'jpg', 'png', 'gif', 'tiff', 'bmp'],
  // imageManagerDeleteURL: '/admin/media/manager-delete',
  // imageManagerDeleteMethod: 'DELETE',
  // imageManagerDeleteParams: { access_token: token },
  // aviaryKey: 'a87e0c3b380943bfb5fb6004fa573ebc',
  videoInsertButtons: ['videoBack', '|', 'videoByURL', 'videoEmbed'],
  imageEditButtons: ['replaceCustomImage', 'imageRemove', '|', 'imageLink', 'linkOpen', 'linkEdit', 'linkRemove', '-', 'imageAlt', 'imageSize', 'imageStyle'], //  'imageStyle', 'aviary'
  htmlAllowedAttrs: ['layout', 'accept', 'accept-charset', 'accesskey', 'action', 'align', 'allowfullscreen', 'allowtransparency', 'alt', 'async', 'autocomplete',
    'autofocus', 'autoplay', 'autosave', 'background', 'bgcolor', 'border', 'charset', 'cellpadding', 'cellspacing', 'checked', 'cite', 'class', 'color', 'cols',
    'colspan', 'content', 'contenteditable', 'contextmenu', 'controls', 'coords', 'data', 'data-.*', 'datetime', 'default', 'defer', 'dir', 'dirname', 'disabled',
    'download', 'draggable', 'dropzone', 'enctype', 'for', 'form', 'formaction', 'frameborder', 'headers', 'height', 'hidden', 'high', 'href', 'hreflang', 'http-equiv',
    'icon', 'id', 'ismap', 'itemprop', 'keytype', 'kind', 'label', 'lang', 'language', 'list', 'loop', 'low', 'max', 'maxlength', 'media', 'method', 'min',
    'mozallowfullscreen', 'multiple', 'muted', 'name', 'novalidate', 'open', 'optimum', 'pattern', 'ping', 'placeholder', 'playsinline', 'poster', 'preload',
    'pubdate', 'radiogroup', 'readonly', 'rel', 'required', 'reversed', 'rows', 'rowspan', 'sandbox', 'scope', 'scoped', 'scrolling', 'seamless', 'selected',
    'shape', 'size', 'sizes', 'span', 'src', 'srcdoc', 'srclang', 'srcset', 'start', 'step', 'summary', 'spellcheck', 'style', 'tabindex', 'target', 'title',
    'type', 'translate', 'usemap', 'value', 'valign', 'webkitallowfullscreen', 'width', 'wrap'],
  events: {
    initialized: function () {
      const context = this;
      scrollPosition.value = window.top.scrollY;

      if (fullscreenMode && !context.fullscreen.isActive()) {
        context.fullscreen.toggle();
      }
      console.log('initialized event');
    }
  }
});

const loadingPreviewMode = ref(false);

const getRTEResources = async () => {
  try {
    const RTEResources = getGraphqlSchema('RTEResources');
    loadingPreviewMode.value = true;
    const result = await apollo.query({
      query: RTEResources,
      variables: { siteWorkspace: store.getters.workspace.code },
      context: { headers: { disableTs: true } },
      fetchPolicy: 'no-cache',
      errorPolicy: 'ignore'
    });

    const resources = result.data[`${store.state.environment.PROJECT}RichTextEditorResources`].data;

    config.value.iframeStyle = `${config.value.iframeStyle} ${resources.css}`;

    if (resources.imageStyles) {
      config.value.imageStyles = resources.imageStyles;
    }

    newValue.value = relativeToAbsoluteUrl(store.getters.workspace.settings.VUE_APP_PREVIEW.url, newValue.value);

    loadingPreviewMode.value = false;
  } catch (e) {

  }
};

// .fr-view > p{
//     border-top: grey 1px dotted;
//     border-bottom: grey 1px dotted;
// }

if (props.field.inlineClasses) {
  config.value.toolbarButtons.moreText.buttons.splice(2, 0, 'inlineClass');
}

// If has static templates it shows button for template insertation
if (templates.value.length) {
  config.value.toolbarButtons.moreText.buttons.push('templateInsertion');
}

const loadingRTETemplates = ref(false);

const getRTEtemplates = async () => {
  try {
    const RTETemplates = getGraphqlSchema('RTETemplates');
    loadingRTETemplates.value = true;
    const result = await apollo.query({
      query: RTETemplates,
      variables: {
        siteWorkspace: store.getters.workspace.code,
        dynamicTemplates: props.field.dynamicTemplates
      },
      context: { headers: { disableTs: true } },
      fetchPolicy: 'no-cache',
      errorPolicy: 'ignore'
    });

    const dynamicTemplates = result.data[`${store.state.environment.PROJECT}RichTextEditorTemplates`].data;

    if (dynamicTemplates.length) {
      templates.value = templates.value.concat(dynamicTemplates.filter(v => v));
    }

    if (!config.value.toolbarButtons.moreText.buttons.includes('templateInsertion')) {
      config.value.toolbarButtons.moreText.buttons.push('templateInsertion');
    }

    loadingRTETemplates.value = false;
  } catch (e) {

  }
};

const options = computed(() => {
  const sortDynamicTemplates = _.orderBy(templates.value, [template => template.name.toLowerCase()], ['asc']);

  const optionsAux = sortDynamicTemplates.reduce((previous, template) => {
    previous[template.uuid] = template.name;
    return previous;
  }, {});

  return optionsAux;
});

const loading = computed(() => loadingRTETemplates.value || loadingPreviewMode.value);

const dataLoaded = ref(false);

const loadData = () => {
  dataLoaded.value = true;
  if (props.field.dynamicTemplates) {
    getRTEtemplates();
  }

  if (props.field.previewMode) {
    getRTEResources();
  }
};

const open = () => {
  if (!dataLoaded.value) {
    loadData();
  }

  show.value = true;
};

if (!fullscreenMode) {
  loadData();
}

FroalaEditor.DefineIcon('insertCustomImage', { NAME: 'image', SVG_KEY: 'insertImage' });
FroalaEditor.RegisterCommand('insertCustomImage', {
  title: 'Insert Image',
  focus: true,
  undo: true,
  attribution: false,
  refreshAfterCallback: true,
  callback: function () {
    console.log(this.html.get());

    const context = this;

    // eslint-disable-next-line no-undef
    cloudinary.openMediaLibrary(
      store.state.cloudinaryConfig,
      {
        insertHandler: data => {
          for (const asset of data.assets) {
          // Gets the path and name of the image (ex: /folderA/folderB/name.jpeg)
            const { public_id } = asset;

            // Gets the derived transformation in case it has one
            let derivation = '';
            if (asset.derived) {
              derivation = `${asset.derived[0].raw_transformation}/`;
            }

            // Gets extension of the file
            const extension = asset.format;

            // Generates the url
            const url = `${derivation}${public_id}.${extension}`;

            context.image.insert(fullUrl(url), false, {}, null, { link: url });
          }
        }
      }
    );

    // setTimeout(() => { context.toolbar.hide(); }, 300);
    // context.toolbar.hide();
  },
  refresh: function ($btn) {
    // console.log(this.selection.element());
  }
});

FroalaEditor.DefineIcon('replaceCustomImage', { NAME: 'image', SVG_KEY: 'replaceImage' });
FroalaEditor.RegisterCommand('replaceCustomImage', {
  title: 'Replace Image',
  focus: true,
  undo: true,
  attribution: false,
  refreshAfterCallback: true,
  callback: function () {
    const context = this;

    // eslint-disable-next-line no-undef
    cloudinary.openMediaLibrary(
      store.state.cloudinaryConfig,
      {
        insertHandler: data => {
          for (const asset of data.assets) {
          // Gets the path and name of the image (ex: /folderA/folderB/name.jpeg)
            const { public_id } = asset;

            // Gets the derived transformation in case it has one
            let derivation = '';
            if (asset.derived) {
              derivation = `${asset.derived[0].raw_transformation}/`;
            }

            // Gets extension of the file
            const extension = asset.format;

            // Generates the url
            const url = `${derivation}${public_id}.${extension}`;

            // .removeAttr('width').removeAttr('height')
            context.image.insert(fullUrl(url), false, {}, context.image.get(), { link: url });
          }
        }
      }
    );

    context.popups.hideAll();
  },
  refresh: function ($btn) {
    // console.log(this.selection.element());
  }
});

FroalaEditor.DefineIcon('close', { NAME: 'image', SVG_KEY: 'close' });
FroalaEditor.RegisterCommand('close', {
  title: 'Close',
  focus: true,
  undo: true,
  attribution: false,
  refreshAfterCallback: true,
  callback: function () {
    console.log('close');
    const context = this;
    if (context.fullscreen.isActive()) {
      context.fullscreen.toggle();
      nextTick(() => {
        window.scrollTo(0, scrollPosition.value);
      });
    }

    show.value = false;

    // context.popups.hideAll();
  },
  refresh: function ($btn) {
    // console.log(this.selection.element());
  }
});

FroalaEditor.DefineIcon('templateInsertion', { NAME: 'template', SVG_KEY: 'insertMore' });
FroalaEditor.RegisterCommand('templateInsertion', {
  title: 'Insert template',
  type: 'dropdown',
  options: () => options.value,
  // html: function () { // If custom html
  //   console.log("html", options.value)
  //   return options.map(o => `Item${o.v}`);
  // },
  focus: true,
  undo: true,
  attribution: false,
  refreshAfterCallback: true,
  callback: function (cmd, val, params) {
    const context = this;
    const template = templates.value.find(t => t.uuid === val);
    if (template) {
      let body = template.html;
      if (props.field.previewMode) {
        body = relativeToAbsoluteUrl(store.getters.workspace.settings.VUE_APP_PREVIEW.url, body);
      }
      context.html.insert(`<p></p>${body}<p></p>`);
    } else {
      console.log('Template not found!');
    }
  },
  refresh: function ($btn) {
    // console.log(this.selection.element());
  }
});

</script>

<style lang="scss" scoped>
</style>
